import { Box } from "@mui/material";
import React, { useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../api/axiosInstance";
import { USER_INITIAL_VALUES, USERS_TABLE_COLUMNS, getRoleName } from "../../data";
import { UserListProps, UserProps } from "../../typings/users";
import CreateUserModal from "./CreateUserModal";
import UsersTable from "./UsersTable";
import { PaginationProps } from "../../typings";

const MySwal = withReactContent(Swal);

type UsersListProps = {
  users: UserListProps[];
  pagination: PaginationProps;
  loading: boolean;
  searchTerm?: string;
  onPageChange: (e: unknown, page: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const UsersList: React.FC<UsersListProps> = ({
  users,
  pagination,
  loading,
  searchTerm = "",
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const [userDetails, setUserDetails] = useState<UserProps>(USER_INITIAL_VALUES);

  const handleEdit = (user: UserProps) => {
    setUserDetails(user);
    handleOpenModal();
  };

  const handleDelete = (user: UserProps) => {
    MySwal.fire({
      title: "Are you sure?",
      html: `
        <div style="text-align: left; margin-bottom: 1rem;">
          <p>You are about to delete the following user:</p>
          <div style="background-color: #f8f9fa; padding: 1rem; border-radius: 8px; margin-top: 1rem;">
            <p><strong>Name:</strong> ${user.name}</p>
            <p><strong>Email:</strong> ${user.email}</p>
            <p><strong>Role:</strong> ${getRoleName(user.role)}</p>
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
      showClass: {
        popup: "animate__animated animate__fadeIn animate__faster",
      },
      hideClass: {
        popup: "animate__animated animate__fadeOut animate__faster",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        api
          .delete(`/users/${user.id}/`)
          .then(() => {
            MySwal.fire({
              title: "Deleted!",
              text: `${user.name} has been deleted.`,
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
          })
          .catch((error) => {
            MySwal.fire({
              title: "Error!",
              text: error.response.data.messages[0],
              icon: "error",
            });
          });
      }
    });
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <UsersTable
        columns={USERS_TABLE_COLUMNS}
        data={users}
        loading={loading}
        searchTerm={searchTerm}
        onEdit={handleEdit}
        onDelete={handleDelete}
        pagination={pagination}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
      <CreateUserModal
        open={isModalOpen}
        onClose={handleCloseModal}
        user={userDetails}
      />
    </Box>
  );
};

export default UsersList;
